"use client"

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { cn } from "@/lib/utils"
import { Subscription } from "@/app/types/Subscription"
import { LoadingSpinner } from "@/components/ui/spinner"
import { Badge } from "@/components/ui/badge"
import { MainGreenButton } from "@/components/buttons/main-green-button"
import { TooltipInfo } from "@/components/ui/tooltip-info"
import { PriceDisplay } from "./price-display"
import { CheckItem } from "./check-item"
import type { Plan, PricingPlanItem } from "./types"
import { TrialActivationButton } from '@/components/trial/trial-activation-button'
import { useTrialContextSafe } from '@/components/trial/trial-context'

interface PricingCardProps {
    plan: Plan;
    isYearly: boolean;
    onPlanSelect: () => void;
    currentSubscription?: Subscription;
    pricing: PricingPlanItem[];
    isLoading: boolean;
    publicMode?: boolean;
    discountApplied?: boolean;
}

export function PricingCard({
    plan,
    isYearly,
    onPlanSelect,
    pricing,
    isLoading,
    publicMode,
    discountApplied,
}: PricingCardProps) {
    const trialContext = useTrialContextSafe();
    const eligibleForTrial = trialContext?.eligibleForTrial ?? false;

    const key = plan.type as 'starter' | 'pro' | 'advanced';
    const selectedPricing = pricing.find(p => p.planType === key && p.interval === (isYearly ? 'year' : 'month'));

    const planHasDiscount = discountApplied && (selectedPricing?.hasDiscount ?? false);
    const price = selectedPricing ? (planHasDiscount ? selectedPricing.price : selectedPricing.originalPrice) : 0;
    const originalPrice = selectedPricing ? selectedPricing.originalPrice : 0;
    const interval = isYearly ? 'year' : 'month';
    const creditsAmount = selectedPricing?.credits;
    const isPlanDisabled = plan.isCurrent;
    const isMostPopular = plan.type === 'pro';

    return (
        <Card className={cn(`w-[340px] rounded-2xl flex flex-col justify-between py-1 px-8 border-main mx-auto sm:mx-0`, {
            "ring-2 ring-main": isMostPopular,
        })} id={plan.title === "Pro" ? "pro-plan-section" : undefined}>
            <div>
                <CardHeader className="py-4 px-0 min-h-[200px]">
                    <div className="flex justify-between items-center">
                        <CardTitle className="text-black font-medium text-lg opacity-80">{plan.title}</CardTitle>
                        {isMostPopular && <Badge className="bg-main rounded-md text-white hover:bg-main">Most Popular</Badge>}
                    </div>
                    <PriceDisplay
                        price={price}
                        interval={interval}
                        plan={plan}
                        discountApplied={planHasDiscount}
                        originalPrice={originalPrice}
                    />
                    <div className="flex flex-col gap-1 pt-1.5 text-natural-500 text-sm">
                        <div className="flex gap-2 items-center">
                            {creditsAmount ? (
                                <>
                                    {creditsAmount.toLocaleString()} credits/{interval}
                                    {plan.maxSeats && plan.maxSeats > 1 && ` (up to ${plan.maxSeats} seats)`}
                                </>
                            ) : plan.description}
                            {plan.type === 'starter' && <TooltipInfo text="25 credits per company, 1 credit per full contact details (email + mobile)." title="Credits usage" />}
                            {plan.type === 'pro' && <TooltipInfo text="25 credits per company, 1 credit per full contact details (email + mobile). Shared credit pool for up to 3 team members." title="Credits usage" />}
                            {plan.type === 'advanced' && <TooltipInfo text="25 credits per company, 1 credit per full contact details (email + mobile). Shared credit pool for up to 6 team members." title="Credits usage" />}
                        </div>
                        {plan.maxSeats === 1 && <span className="text-xs">{plan.maxSeats} seat</span>}
                        {plan.maxSeats && plan.maxSeats > 1 && <span className="text-xs">{plan.maxSeats} seats</span>}
                    </div>
                </CardHeader>
                <CardContent className="flex flex-col gap-2 px-0">
                    {!publicMode && eligibleForTrial ? (
                        <TrialActivationButton
                            className="w-full mt-0"
                            title="Start Trial"
                            buttonClassName="w-full"
                        />
                    ) : (
                        <MainGreenButton
                            disabled={!publicMode && isPlanDisabled}
                            className="w-full items-center justify-center"
                            onClick={onPlanSelect}
                        >
                            {isLoading ? <LoadingSpinner /> : publicMode ? 'Start Trial' : plan.actionLabel}
                        </MainGreenButton>
                    )}
                    {plan.features.map((feature, index) => (
                        <div key={index} className="flex items-center gap-1">
                            <CheckItem
                                text={feature.text}
                                className={`w-full ${index === 0 ? "font-bold" : ""}`}
                                tooltip={feature.tooltip}
                                isNotIncluded={feature.isNotIncluded}
                            />
                        </div>
                    ))}
                </CardContent>
            </div>
        </Card>
    );
}
